import React from 'react';
import { useSelector } from 'react-redux';
import PolarCheckout from './PolarCheckout';

const SubscriptionStatus = () => {
  const subscription = useSelector((state) => state.subscription);
  
  const {
    status,
    plan_name,
    current_period_end,
    days_until_expiry,
    expires_soon,
    payment_status,
    renewal_required,
    has_access,
    is_active
  } = subscription;

  // Format the expiration date
  const formatExpirationDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get status color and icon
  const getStatusDisplay = () => {
    if (renewal_required) {
      return {
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        icon: '⚠️',
        text: status === 'cancelled' ? 'Subscription Cancelled' : 'Renewal Required'
      };
    } else if (expires_soon) {
      return {
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        icon: '⏰',
        text: 'Expires Soon'
      };
    } else if (is_active) {
      return {
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        icon: '✅',
        text: 'Active'
      };
    } else {
      return {
        color: 'text-gray-600',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        icon: '💤',
        text: 'Inactive'
      };
    }
  };

  // Get payment status display with enhanced mapping
  const getPaymentStatusDisplay = () => {
    switch (payment_status) {
      case 'current':
        return { color: 'text-green-600', icon: '💳', text: 'Payment Current' };
      case 'active':
        return { color: 'text-green-600', icon: '✅', text: 'Payment Active' };
      case 'failed':
        return { color: 'text-red-600', icon: '❌', text: 'Payment Failed' };
      case 'expired':
        return { color: 'text-yellow-600', icon: '⏰', text: 'Expiring Soon' };
      case 'expiring_soon':
        return { color: 'text-yellow-600', icon: '⚠️', text: 'Expiring Soon' };
      case 'cancelled':
        return { color: 'text-gray-600', icon: '🚫', text: 'Payment Cancelled' };
      case 'none':
        return { color: 'text-gray-500', icon: '💤', text: 'No Payment Method' };
      case 'unknown':
        // Better handling for unknown status - provide more context
        if (has_access && is_active) {
          return { color: 'text-green-600', icon: '✅', text: 'Payment Active' };
        } else if (has_access) {
          return { color: 'text-blue-600', icon: '📋', text: 'Subscription Active' };
        } else {
          return { color: 'text-gray-500', icon: '❓', text: 'Status Pending' };
        }
      default:
        // Enhanced fallback with more context
        if (has_access && is_active) {
          return { color: 'text-green-600', icon: '✅', text: 'Payment Active' };
        } else {
          return { color: 'text-gray-600', icon: '❓', text: 'Payment Status Unknown' };
        }
    }
  };

  const statusDisplay = getStatusDisplay();
  const paymentDisplay = getPaymentStatusDisplay();

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Subscription Status</h3>
      
      <div className="space-y-4">
        {/* Main Status Card */}
        <div className={`p-4 rounded-lg border ${statusDisplay.bgColor} ${statusDisplay.borderColor}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{statusDisplay.icon}</span>
              <div>
                <h4 className={`font-semibold ${statusDisplay.color}`}>
                  {statusDisplay.text}
                </h4>
                <p className="text-sm text-gray-600">
                  {plan_name || 'No Plan Selected'}
                </p>
              </div>
            </div>
            
            {/* Renewal/Upgrade Actions */}
            {(renewal_required || !has_access) && (
              <div className="flex space-x-2">
                <PolarCheckout planType="monthly">
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
                    Renew Monthly ($19)
                  </button>
                </PolarCheckout>
                <PolarCheckout planType="yearly">
                  <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
                    Renew Yearly ($199)
                  </button>
                </PolarCheckout>
              </div>
            )}
          </div>
        </div>

        {/* Subscription Details Grid */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Expiration Information */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h5 className="font-medium text-gray-900">Expiration Date</h5>
            </div>
            <p className="text-sm text-gray-600 mb-1">
              {formatExpirationDate(current_period_end)}
            </p>
            {days_until_expiry !== null && (
              <p className={`text-sm font-medium ${
                days_until_expiry <= 0 ? 'text-red-600' : 
                days_until_expiry <= 7 ? 'text-yellow-600' : 
                'text-green-600'
              }`}>
                {days_until_expiry <= 0 
                  ? 'Expired'
                  : days_until_expiry === 1 
                    ? '1 day remaining'
                    : `${days_until_expiry} days remaining`
                }
              </p>
            )}
          </div>

          {/* Payment Status */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              <h5 className="font-medium text-gray-900">Payment Status</h5>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-lg">{paymentDisplay.icon}</span>
              <span className={`text-sm font-medium ${paymentDisplay.color}`}>
                {paymentDisplay.text}
              </span>
            </div>
          </div>
        </div>

        {/* Renewal Warning Banner */}
        {expires_soon && !renewal_required && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-yellow-800 mb-1">
                  Subscription Expiring Soon
                </h4>
                <p className="text-sm text-yellow-700 mb-3">
                  Your subscription expires in {days_until_expiry} {days_until_expiry === 1 ? 'day' : 'days'}. 
                  Renew now to avoid interruption of your GitHub streak automation.
                </p>
                <div className="flex space-x-2">
                  <PolarCheckout planType="monthly">
                    <button className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors">
                      Renew Monthly
                    </button>
                  </PolarCheckout>
                  <PolarCheckout planType="yearly">
                    <button className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors">
                      Upgrade to Yearly
                    </button>
                  </PolarCheckout>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Expired/Renewal Required Banner */}
        {renewal_required && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-red-800 mb-1">
                  {status === 'cancelled' ? 'Subscription Cancelled' : 'Subscription Expired'}
                </h4>
                <p className="text-sm text-red-700 mb-3">
                  {status === 'cancelled' 
                    ? 'Your subscription has been cancelled. GitHub integration features are no longer available.'
                    : 'Your subscription has expired. Renew now to restore access to GitHub streak automation.'
                  }
                </p>
                <div className="flex space-x-2">
                  <PolarCheckout planType="monthly">
                    <button className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors">
                      Renew Monthly ($19)
                    </button>
                  </PolarCheckout>
                  <PolarCheckout planType="yearly">
                    <button className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm font-medium transition-colors">
                      Renew Yearly ($199)
                    </button>
                  </PolarCheckout>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Active Subscription Upgrade Prompt */}
        {is_active && !renewal_required && subscription.plan_type === 'monthly' && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-blue-800">Save with Yearly Plan</h4>
                  <p className="text-sm text-blue-700">
                    Upgrade to yearly and save $29 + get premium analytics
                  </p>
                </div>
              </div>
              <PolarCheckout planType="yearly">
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors">
                  Upgrade to Yearly
                </button>
              </PolarCheckout>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionStatus;